import { DetailedHTMLProps, VideoHTMLAttributes, useEffect, useRef, useState } from "react";
import { AspectRatio, Box, Flex, HStack, useColorMode } from "@chakra-ui/react";
import { useInView } from "framer-motion";
import throttle from "lodash/throttle";
import { Text } from "uikit";
import { AppleStoreLink, PlayStoreLink } from "components/AppStoreLink";
import { useIsMobile, TextContent, SectionContainer } from "./shared";

const videoWidth = 1400;
const videoHeight = 1000;

const usePlayOnScroll = () => {
  const { colorMode } = useColorMode();
  const [timesPlayed, setTimesPlayed] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isInView = useInView(videoRef);

  // When the user starts scrolling play the video once
  useEffect(() => {
    const handleFirstScroll = () => {
      videoRef.current?.play();
    };

    window.addEventListener("scroll", handleFirstScroll, { once: true });
    return () => {
      // I am not sure this is necessary with the "once" option
      window.removeEventListener("scroll", handleFirstScroll);
    };
  }, [videoRef, colorMode, timesPlayed]);

  // Reset the video when it leaves the viewport so when you scroll back up
  // the video is reset
  useEffect(() => {
    if (!isInView && !!videoRef.current) {
      videoRef.current.load();
    }
  }, [videoRef, isInView]);

  // Attach the one-time handler again if the use scrolls all the way up
  useEffect(() => {
    const handleScroll = throttle(() => {
      if (typeof window !== "undefined" && window.scrollY === 0) {
        setTimesPlayed((prevTimesPlayed) => prevTimesPlayed + 1);
      }
    }, 200);

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [setTimesPlayed]);

  return videoRef;
};

type HeroVideoProps = DetailedHTMLProps<VideoHTMLAttributes<HTMLVideoElement>, HTMLVideoElement>;

const HeroVideo = (props: HeroVideoProps) => {
  const { colorMode } = useColorMode();
  const videoRef = usePlayOnScroll();
  const fileName = `hero-${colorMode}-compressed.mp4`;
  const poster = `poster-${colorMode}.png`;
  return (
    <video
      ref={videoRef}
      loop={false}
      muted
      disablePictureInPicture
      playsInline
      key={fileName}
      poster={`/images/app-landing-page/${poster}`}
      {...props}
    >
      <source type="video/mp4" src={`/videos/app-landing-page/${fileName}`} />
    </video>
  );
};

const Content = () => (
  <Flex flexDirection="column">
    <Box order={{ base: 1, md: 2 }}>
      <Text
        as="h1"
        fontSize={{ base: "1.75rem", md: "2.25rem", xl: "3rem" }}
        lineHeight={{ base: "2.5rem", md: "3rem", xl: "3.5rem" }}
        maxWidth={{ base: "auto", md: "420px", xl: "560px" }}
        bold
        mb={4}
      >
        Because Your Monitor Won&apos;t Fit In Your Pocket.
      </Text>
      <TextContent mb={4} maxWidth={{ base: "auto", md: "497px", xl: "780px" }}>
        <TextContent as="span" color="text-01" bold>
          The NFT ecosystem at a glance.
        </TextContent>{" "}
        Stay on top of your portfolio, checkout what&apos;s trending, and discover new collections the second they drop.
        Get LooksRare&apos;s all-new mobile app now.
      </TextContent>
    </Box>
    <HStack spacing={2} mb={4} order={{ base: 2, md: 1 }}>
      <AppleStoreLink />
      <PlayStoreLink />
    </HStack>
  </Flex>
);

export const MobileHero = () => (
  <>
    <SectionContainer baseMaxWidth="390px" mb={0} zIndex={10} position="relative">
      <Content />
    </SectionContainer>
    <Box overflowX="hidden" top="-110px" position="relative" zIndex={1}>
      <Box width="865px" height="600px" position="relative">
        <HeroVideo
          style={{
            position: "absolute",
            top: 0,
            left: "-432px", // Half of the video is off the screen
            height: "100%",
            width: "100%",
            objectFit: "contain",
          }}
        />
      </Box>
    </Box>
  </>
);

export const DesktopHero = () => (
  <SectionContainer baseMaxWidth="390px">
    <Box position="relative">
      <AspectRatio ratio={videoWidth / videoHeight}>
        <HeroVideo />
      </AspectRatio>
      <Box position="absolute" left={0} bottom={0}>
        <Content />
      </Box>
    </Box>
  </SectionContainer>
);

export const Hero = () => {
  const isMobile = useIsMobile();
  return isMobile ? <MobileHero /> : <DesktopHero />;
};
